'use client';

import { LAYOUT } from '@/features/marketing/components/_styles';
import React, { useState } from 'react';
import Link from 'next/link';
import {
  FOOTER_CONFIG,
  FOOTER_SECTIONS,
  SOCIAL_LINKS,
} from '@/features/marketing/data/navigation.data';
import { SocialIcon } from '@/features/marketing/components/SocialIcon';

export function EchoFooter() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => {
        setEmail('');
        setSubscribed(false);
      }, 3000);
    }
  };

  return (
    <footer className={`w-full ${LAYOUT.panelTop} bg-black text-white pt-24 sm:pt-32 pb-16 overflow-hidden relative`}>
      <div className={`${LAYOUT.wide} ${LAYOUT.gutter}`}>
        {/* Top Header / CTA Row */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pb-14">
          {/* Left Title */}
          <div className="flex flex-col items-start text-left max-w-4xl">
            <h2 className="text-4xl sm:text-6xl lg:text-[76px] font-normal leading-[1.04] tracking-[-0.035em] text-white">
              {FOOTER_CONFIG.headline}
              <br />
              {FOOTER_CONFIG.subheadline}
            </h2>
            <p className="mt-5 max-w-[640px] text-base sm:text-xl lg:text-[22px] font-light leading-[1.45] text-white/80">
              {FOOTER_CONFIG.description}
            </p>
          </div>

          {/* Right Newsletter & Socials */}
          <div className="w-full lg:w-auto lg:min-w-[340px] max-w-sm flex flex-col gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#7d7d7d]">
                {FOOTER_CONFIG.newsletterEyebrow}
              </span>
              <p className="text-xs text-[#a9a9a9] mt-1">
                {FOOTER_CONFIG.newsletterDescription}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="relative flex items-center">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={FOOTER_CONFIG.newsletterPlaceholder}
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-[#7d7d7d] focus:outline-none focus:border-white/40 transition-colors pr-28"
              />
              <button
                type="submit"
                className="absolute right-1.5 px-3 py-1.5 bg-white hover:bg-stone-200 text-[#181a1a] text-xs font-medium rounded-none transition-all"
              >
                {subscribed ? FOOTER_CONFIG.newsletterSuccessText : FOOTER_CONFIG.newsletterButtonText}
              </button>
            </form>

            {/* Social Icons */}
            <div className="flex items-center gap-3 pt-1">
              <span className="text-xs text-[#7d7d7d]">{FOOTER_CONFIG.followLabel}</span>
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="size-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  aria-label={social.name}
                >
                  <SocialIcon icon={social.icon} className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation & Categorized Directory Grid */}
        <div className="py-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 sm:gap-10">
          {FOOTER_SECTIONS.map((section) => (
            <div key={section.title} className="flex flex-col gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#7d7d7d]">
                {section.title}
              </span>
              {section.links.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-xs text-[#a9a9a9] hover:text-white transition-colors"
                  {...(link.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ))}
        </div>

      </div>

      {/* Edge-to-edge rail; the copyright stays on the content grid. */}
      <div className="border-t border-white/10">
        <div className={`${LAYOUT.wide} ${LAYOUT.gutter} pt-8 text-xs text-[#7d7d7d]`}>
          <p>{FOOTER_CONFIG.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
